/**
 * Hosts {@link RemittancePanel} on the banking page.
 *
 * The panel is connection-scoped — the lockbox reports for a whole
 * banking relationship rather than per account — but nothing else on
 * this page has a notion of a *selected* connection. So this is the
 * small amount of state that makes one: load the company's connections,
 * show the first, and offer a picker only when there is more than one.
 *
 * WHY IT LIVES BESIDE THE CONNECTION LIST rather than inside it: a panel
 * per row would mean one fetch per connection on page load, and every
 * one of those is a query against a table that only grows. A manager
 * looking at lockbox payments is looking at one relationship at a time.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Subheading } from './common/Catalyst/heading';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { ConnectionPicker } from './bank/linear/ConnectionPicker';
import { bankService } from './clients/ApiClients';
import { BankConnectionReturn } from './clients/rust-api-client';
import { getCompanyIdFromToken } from './utils/TokenUtils';
import { getBankErrorParts } from './utils/bankHelpers';
import RemittancePanel from './RemittancePanel';

const RemittanceSection: React.FC = () => {
  const companyId = useMemo(() => getCompanyIdFromToken(), []);
  const [connections, setConnections] = useState<BankConnectionReturn[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      const response = await bankService.listCompanyConnections({
        companyId,
      });
      const active = response.data.filter((c) => c.is_active);
      setConnections(active);
      setSelected((current) => current ?? active[0]?.id ?? null);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setError(body || "Couldn't load bank connections.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return null;

  if (error) {
    return <ErrorAlert className="mt-4">{error}</ErrorAlert>;
  }

  // No connection means no lockbox, which is a different fact from a
  // lockbox with nothing in it — and saying so beats an empty table.
  if (connections.length === 0) {
    return (
      <div className="mt-8">
        <Subheading>Lockbox payments</Subheading>
        <Text className="mt-1">
          No active bank connection, so there is no lockbox to read.
        </Text>
      </div>
    );
  }

  return (
    <div>
      {connections.length > 1 && (
        <div className="mt-8 max-w-sm">
          <Subheading>Lockbox for</Subheading>
          <ConnectionPicker
            className="mt-2 border border-line"
            connections={connections}
            value={selected}
            onChange={setSelected}
          />
        </div>
      )}
      {selected && <RemittancePanel connectionId={selected} />}
    </div>
  );
};

export default RemittanceSection;
